/**
 * Outreach tab — sleek monochrome UI kit (Stage 3.5 revamp).
 *
 * The owner's redirect: drop coral entirely for a clean, modern, Attio/Stripe/
 * Notion feel — near-black `#191919` for the single primary action, white
 * surfaces, hairline borders, one restrained indigo accent (`#5B5BD6`) reserved
 * for AI affordances. Tailwind-only (no inline styles); these primitives keep
 * that language DRY across the Prospects + Campaign surfaces.
 *
 * The shell also remaps the Lantern `--accent*` CSS vars to this monochrome
 * palette (see OUTREACH_THEME), so REUSED hub components (SequenceTreeEditor,
 * MessagePreview, OutreachAccountPicker…) render sleek inside the tab without
 * being forked — their coral comes from `var(--accent)`, which we override.
 */

import type { ReactNode } from 'react';
import { Loader2, Search } from 'lucide-react';

/**
 * Tailwind arbitrary-property classes that remap the accent CSS vars to the
 * monochrome palette for everything mounted under the shell. Applied on the
 * outreach root so reused `var(--accent)` components go black, not coral.
 * Scoped to the tab — the standalone hub is unaffected.
 */
export const OUTREACH_THEME =
    '[--accent:#191919] [--accent-emphasis:#000000] ' +
    '[--accent-muted:rgba(0,0,0,0.06)] [--accent-subtle:rgba(0,0,0,0.035)] ' +
    '[--border-accent:rgba(0,0,0,0.16)] [--text-link:#191919] ' +
    '[--surface-selected:rgba(0,0,0,0.035)]';

/* ─── buttons ─── */

type BtnVariant = 'primary' | 'secondary' | 'ghost';
const BTN_BASE =
    'inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed';
const BTN_VARIANT: Record<BtnVariant, string> = {
    primary:
        'bg-[#191919] text-white shadow-[0_1px_2px_rgba(15,15,15,0.12)] hover:bg-black disabled:bg-[#F1F1F2] disabled:text-[#C4C5C8] disabled:shadow-none',
    secondary:
        'border border-black/[0.09] bg-white text-[#37352F] shadow-[0_1px_1px_rgba(15,15,15,0.04)] hover:bg-[#FAFAFA] disabled:opacity-50',
    ghost: 'bg-transparent text-[#5F6165] hover:bg-black/[0.04] disabled:opacity-50',
};
const BTN_SIZE = {
    sm: 'h-7 px-2.5 text-[12px]',
    md: 'h-8 px-3 text-[12.5px]',
    lg: 'h-9 px-4 text-[13.5px]',
} as const;

export function Btn({
    children,
    variant = 'secondary',
    size = 'md',
    disabled,
    onClick,
    title,
    className = '',
    type = 'button',
}: {
    children: ReactNode;
    variant?: BtnVariant;
    size?: keyof typeof BTN_SIZE;
    disabled?: boolean;
    onClick?: () => void;
    title?: string;
    className?: string;
    type?: 'button' | 'submit';
}) {
    return (
        <button
            type={type}
            title={title}
            disabled={disabled}
            onClick={onClick}
            className={`${BTN_BASE} ${BTN_VARIANT[variant]} ${BTN_SIZE[size]} ${className}`}
        >
            {children}
        </button>
    );
}

/* ─── toggle switch (ink when on) ─── */

export function Switch({ on, onChange, label }: { on: boolean; onChange: (next: boolean) => void; label?: string }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={label}
            onClick={() => onChange(!on)}
            className={`flex h-5 w-[34px] shrink-0 items-center rounded-full p-0.5 transition-colors ${
                on ? 'justify-end bg-[#191919]' : 'justify-start bg-[#E2E2E5]'
            }`}
        >
            <span className="h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(15,15,15,0.2)]" />
        </button>
    );
}

/* ─── search input ─── */

export function SearchInput({
    value,
    onChange,
    placeholder = 'Search people…',
    className = '',
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    className?: string;
}) {
    return (
        <div
            className={`flex h-8 items-center gap-2 rounded-lg border border-black/[0.09] bg-white px-2.5 shadow-[0_1px_1px_rgba(15,15,15,0.04)] focus-within:border-black/20 ${className}`}
        >
            <Search size={14} strokeWidth={2} className="shrink-0 text-[#AEB0B4]" />
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="min-w-0 flex-1 border-none bg-transparent text-[12.5px] text-[#191919] outline-none placeholder:text-[#AEB0B4]"
            />
        </div>
    );
}

/* ─── section label ─── */

export function SectionLabel({ children, className = '' }: { children: ReactNode; className?: string }) {
    return (
        <div className={`text-[10.5px] font-bold uppercase tracking-[0.9px] text-[#AEB0B4] ${className}`}>
            {children}
        </div>
    );
}

/* ─── count pill ─── */

export function CountPill({ n }: { n: number }) {
    return (
        <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[#F1F1F2] px-1.5 text-[10.5px] font-bold text-[#5F6165] tabular-nums">
            {n.toLocaleString()}
        </span>
    );
}

/* ─── dot chip (status) ───
   `dotClass` is a LITERAL Tailwind bg utility (e.g. 'bg-[#1E9E54]') so the JIT
   sees it at build time — dynamic hex interpolation wouldn't generate a class.
   Enriching is the only spinner case, tinted with the indigo AI accent. */

export function DotChip({ dotClass, spin, children }: { dotClass: string; spin?: boolean; children: ReactNode }) {
    return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-[#F5F5F6] py-0.5 pl-2 pr-2.5 text-[11.5px] font-medium text-[#5F6165]">
            {spin ? (
                <Loader2 size={10} className="animate-spin text-[#5B5BD6]" />
            ) : (
                <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
            )}
            {children}
        </span>
    );
}
